import type { Asset } from '@schibsted-svp/svp-api-types';

import { getAssetMetadata, isLiveStream } from '../../../../utils/player/asset';
import { PlaylistApiClient } from '../../services/playlistApi';
import { RelatedApiClient } from '../../services/relatedApi';
import { isEpisode } from '../../utils/assetHelpers';
import { ConfigContextProps } from '../ConfigProvider';

const RELATED_ASSETS_LIMIT = 10;

const PlaylistCacheMap = new Map<string, Asset[]>();

async function getPlaylist({
  playlist,
  playlistApi,
}: {
  playlist: Exclude<ConfigContextProps['playlist'], undefined>;
  playlistApi: PlaylistApiClient;
}) {
  if (typeof playlist === 'string') {
    const cachedPlaylist = PlaylistCacheMap.get(playlist);

    if (cachedPlaylist) {
      return cachedPlaylist;
    }

    const fetchedPlaylist = await playlistApi.getPlaylist(playlist);
    PlaylistCacheMap.set(playlist, fetchedPlaylist);

    return fetchedPlaylist;
  }

  return playlist;
}

export async function getNextAssets({
  asset,
  relatedApi,
  playlistApi,
  config,
}: {
  asset: Asset | undefined;
  relatedApi: RelatedApiClient;
  playlistApi: PlaylistApiClient;
  config: ConfigContextProps;
}) {
  if (
    !asset ||
    isLiveStream(asset) ||
    getAssetMetadata(asset).disableNextVideo === 'true' ||
    (asset.provider === 'brandstudio' && !isEpisode(asset))
  ) {
    return [];
  }

  if (config.playlist) {
    const playlistAssets = await getPlaylist({
      playlist: config.playlist,
      playlistApi,
    });

    const index = playlistAssets.findIndex(({ id }) => id === asset.id);

    if (index !== -1) {
      return [
        ...playlistAssets.slice(index + 1),
        ...playlistAssets.slice(0, index),
      ];
    }

    return playlistAssets;
  }

  return relatedApi.getRelatedAssets({
    assetId: asset.id,
    limit: RELATED_ASSETS_LIMIT,
  });
}
